// src/stores/settings.ts
import { create } from 'zustand'

import apiClient from '@/api/axios'

import { logger } from '@/lib/logger'

import { API_ENDPOINTS } from '../api/endpoints'
import { deepMerge } from '../lib/utils'

type AppSettingsState = {
  settings: Record<string, any>
  loading: boolean
  error: string | null
  fetchSettings: (isAuthenticated?: boolean) => Promise<void>
  updateSettings: (newSettings: Record<string, any>) => Promise<void>
  getSetting: <T>(key: string, defaultValue?: T) => T
  resetSettings: () => Promise<void>
}

export const useAppSettings = create<AppSettingsState>((set, get) => ({
  settings: {},
  loading: false,
  error: null,

  // Fetch settings from API - automatically uses public endpoint if not authenticated
  fetchSettings: async (isAuthenticated = true) => {
    set({ loading: true, error: null })
    try {
      // Switch endpoint based on authentication status
      const endpoint = isAuthenticated ? API_ENDPOINTS.appSettings.base : API_ENDPOINTS.appSettings.public
      const res = await apiClient.get(endpoint)
      logger.debug('useAppSettings fetchSettings', { isAuthenticated, endpoint, data: res?.data })
      set({ settings: res?.data?.data?.data, loading: false, error: null })
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Failed to load settings'
      logger.error('Failed to fetch app settings', err)
      set({ error, loading: false })
      throw new Error(error)
    }
  },

  // Update settings via API (authenticated only)
  updateSettings: async (newSettings) => {
    set({ loading: true, error: null })
    try {
      const currentSettings = get().settings
      const mergedSettings = deepMerge(currentSettings, newSettings)
      const res = await apiClient.put(API_ENDPOINTS.appSettings.base, { settings: mergedSettings })
      logger.debug('useAppSettings updateSettings', res?.data)
      set({ settings: res?.data?.data?.data, loading: false })
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to update settings',
        loading: false,
      })
      throw err
    }
  },

  // Get a specific setting with dot notation support
  getSetting: <T>(key: string, defaultValue?: T): T => {
    const { settings } = get()
    const value = key.split('.').reduce((obj, k) => (obj || {})[k], settings)
    return (value !== undefined ? value : defaultValue) as T
  },

  // Reset settings via API (authenticated only)
  resetSettings: async () => {
    set({ loading: true, error: null })
    try {
      const res = await apiClient.post(API_ENDPOINTS.appSettings.reset)
      logger.debug('useAppSettings resetSettings', res?.data)
      set({ settings: res?.data?.data?.data, loading: false })
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to reset settings',
        loading: false,
      })
      throw err
    }
  },
}))

// Utility hook for easy access to settings
export const useSetting = <T>(key: string, defaultValue?: T): T => {
  return useAppSettings((state) => state.getSetting<T>(key, defaultValue))
}
